<script context="module" lang="ts">
	export interface CollectionInterface {
		getTranslations: () => TranslationDTO[];
		rewardable?: boolean;
		taxable?: boolean;
	}
</script>

<script lang="ts">
	// UI
	import Item from "./Item.svelte";
	import {
		SetupIcon,
		DefaultCheckbox,
		PlusIcon,
		OutlineButton,
		Untaxable,
		Unrewardable,
	} from "@hitit/new_ui/src";
	// Utils
	import { createEventDispatcher, onDestroy, tick } from "svelte";
	import {
		getKeyInTranslation,
		isOverflown,
		sortableAction,
	} from "../../utils";
	// Types
	import type { ItemInteface } from "./Item.svelte";
	import type { TranslationDTO } from "../../class/TranslationDTO";
	import type { SortableEvent } from "sortablejs";
	import Sortable from "sortablejs";
	// Props
	export let editmode = false;
	export let multipleSelectMode = false;
	export let hasSetup = false;
	export let collection: CollectionInterface;
	export let items: ItemInteface[] = [];
	export let selectedItems: ItemInteface[] = [];
	export let sortDisable = true;
	export let afterSortingstart = (e: SortableEvent) => {};
	export let afterSortingend = (e: SortableEvent) => {};

	// Constants
	const dispatch = createEventDispatcher();
	const setupClickEvent = () => {
		dispatch("COLLECTION_SETUP_EVENT", collection);
	};

	const newItemClickEvent = () => {
		dispatch("NEW_ITEM_EVENT", collection);
	};

	const itemMultipleSelectHandler = (e: { detail: ItemInteface }) => {
		const item = e.detail;

		if (selectedItems.find((v) => item === v)) {
			selectedItems = selectedItems.filter((v) => v !== item);
		} else {
			selectedItems = [...selectedItems, e.detail];
		}
	};

	const allInside = (a: ItemInteface[], b: ItemInteface[]) => {
		return a.reduce((prev, cur) => {
			if (!prev) {
				return false;
			}

			return Boolean(b.find((v) => cur === v));
		}, true);
	};

	const multipleSelectHandler = () => {
		if (allInside(items, selectedItems)) {
			const filteredItem = selectedItems.filter((v) => {
				const selectedItemID = v.id;
				const notInSelectedItem =
					items.find((v) => v.id === selectedItemID) === undefined;
				return notInSelectedItem;
			});
			selectedItems = filteredItem;
		} else {
			const filteredItem = items.filter((v) => {
				const selectedItemID = v.id;
				const notInSelectedItem =
					selectedItems.find((v) => v.id === selectedItemID) === undefined;
				return notInSelectedItem;
			});
			selectedItems = [...selectedItems, ...filteredItem];
		}
	};

	let selectedDIVs: HTMLDivElement[] = [];

	$: sortableOption = {
		group: {
			name: "category",
			put: true,
			pull: false,
		},
		disabled: sortDisable,
		delay: 50,
		multiDrag: multipleSelectMode, // Enable multi-drag
		selectedClass: multipleSelectMode ? "dragging-select" : undefined,
		avoidImplicitDeselect: true,
		animation: 150,
		swapThreshold: 0.65,
		forceFallback: true,
		ghostClass: "sortable-ghost",
		dragClass: "sortable-drag",
		draggable: ".item",
		filter: multipleSelectMode ? ".item:not(.selected)" : "#add-item",
		preventOnFilter: false,
		onSort,
		onStart,
		onEnd,
		onDeselect,
	};
	function onSort(event: SortableEvent) {
		const { oldIndex, newIndex, oldIndicies, newIndicies } = event;
		if (multipleSelectMode) {
			dispatch("UPDATED_MULTIPLE_ITEM_DISPLAY_SORT", {
				oldIndicies,
				newIndicies,
			});
		} else {
			dispatch("UPDATED_ITEM_DISPLAY_SORT", { oldIndex, newIndex });
		}
	}
	function onStart(event: SortableEvent) {
		document.body.classList.add("dragging");
		afterSortingstart(event);
	}

	function mobileTouchend(e: any) {
		const { pageX, pageY } = e;
		const collections = [
			...(document.querySelectorAll(
				".-collection"
			) as NodeListOf<HTMLButtonElement>),
		];
		const nearstEL = collections.find((v) => {
			const rect = v.getBoundingClientRect();
			const realLeft = v.offsetLeft;
			const realTop = v.offsetTop;
			const horizontalCondition =
				pageX >= realLeft - 15 && realLeft + rect.width + 15 >= pageX;
			const verticalCondition =
				pageY >= realTop - 15 && realTop + rect.height + 15 >= pageY;

			return horizontalCondition && verticalCondition;
		});

		if (nearstEL) {
			const button = nearstEL;
			const customEvent = new Event("touchend");
			button.dispatchEvent(customEvent);
		}
		document.removeEventListener("touchend", mobileTouchend);
		afterSortingend(e);
	}

	function onEnd(
		event: SortableEvent & { originalEvent: MouseEvent | TouchEvent }
	) {
		document.body.classList.remove("dragging");
		const { oldIndicies, newIndicies } = event;
		const originalEvent = event.originalEvent;
		if (multipleSelectMode) {
			//Multiple Sort시 연속되지 않는 그룹을 선택 후, 제자리에 두는 경우, sort이벤트가 작동하지 않는다.
			const oldIndexies = oldIndicies.map((v) => v.index).join("");
			const newIndexies = newIndicies.map((v) => v.index).join("");

			oldIndexies !== newIndexies &&
				dispatch("UPDATED_MULTIPLE_ITEM_DISPLAY_SORT", {
					oldIndicies,
					newIndicies,
				});
		}

		originalEvent.type === "mouseup" && afterSortingend(event);
		originalEvent.type === "touchend" && mobileTouchend(originalEvent);
	}
	function onDeselect(e: SortableEvent) {
		upToData(selectedItems, items);
	}

	async function upToData(
		selectedItemList: ItemInteface[],
		items: ItemInteface[]
	) {
		for (const div of selectedDIVs) {
			Sortable.utils.deselect(div);
		}

		await tick(); //Wait for dom render.
		const selectedPresentItem = items.filter((v) => {
			const id = v.id;
			const condition = selectedItemList.find((v) => v.id === id) !== undefined;
			return condition;
		});

		const presentDivs = selectedPresentItem
			.map((v) => document.getElementById(v.id || ""))
			.filter((v) => v != null) as HTMLDivElement[];

		for (const div of presentDivs) {
			Sortable.utils.select(div as HTMLElement);
		}
		selectedDIVs = presentDivs;
	}

	$: {
		if (editmode && itemsContainer && addButton) {
			if (isOverflown(itemsContainer)) {
				itemsContainer.scrollTop = itemsContainer.scrollHeight;
			} else {
				window.scrollTo(0, document.body.scrollHeight);
			}
		}
	}
	$: upToData(selectedItems, items);
	let itemsContainer: HTMLDivElement;
	let addButton: HTMLDivElement;

	onDestroy(() => {
		document.removeEventListener("touchend", mobileTouchend);
	});
</script>

<div class="container">
	<div class="controler-name">
		{#if editmode}
			{#if multipleSelectMode}
				<div class="multipleSelect" on:click={multipleSelectHandler}>
					<DefaultCheckbox checked={allInside(items, selectedItems)} />
				</div>
			{:else}
				<div class="setupicon">
					<div id="setupbutton" on:click={setupClickEvent}>
						<SetupIcon fill={hasSetup ? "#6d6f76" : "#bcbfc3"} />
					</div>
					<slot name="setup" />
				</div>
			{/if}
		{/if}
		<div class="name">
			{`${getKeyInTranslation(collection.getTranslations(), "name", "ko")} (${
				items.length
			})`}
		</div>
		<div class="icons">
			{#if collection.rewardable === false}
				<span class="icon" t-id="unrewardable">
					<Unrewardable />
				</span>
			{/if}
			{#if collection.taxable === false}
				<span class="icon" t-id="untaxable">
					<Untaxable />
				</span>
			{/if}
			<!--  -->
		</div>
	</div>
	<div
		class="items"
		bind:this={itemsContainer}
		use:sortableAction={sortableOption}
	>
		{#each items as item (item.id)}
			<Item
				on:ITEM_CLICK_EVENT
				on:ITEM_DELETE_CLICK_EVENT
				on:ITEM_MULTIPLE_SELECT_CLICK={itemMultipleSelectHandler}
				selected={item === selectedItems.find((v) => item === v)}
				editmode={editmode}
				multipleSelectMode={multipleSelectMode}
				item={item}
			/>
		{/each}
		{#if editmode && !multipleSelectMode}
			<div class="add-item" bind:this={addButton}>
				<OutlineButton
					noneBordercolor
					fitmode
					nonePadding
					on:click={newItemClickEvent}
				>
					<div class="inner">
						<div class="add-sign">
							<PlusIcon thin />
						</div>
					</div>
				</OutlineButton>
			</div>
		{/if}
	</div>
</div>

<style lang="scss">
	@import "../../../variables.scss";
	.container {
		display: flex;
		flex-direction: column;
		gap: 10px;

		.controler-name {
			display: flex;
			gap: 10px;
			align-items: center;

			.name {
				display: flex;
				align-items: center;
				word-break: break-all;

				font-weight: 500;
				font-size: 1.25rem;
				color: #46474b;
			}
			.icons {
				display: flex;
				padding-top: 4px;
				gap: 5px;
				.icon {
					width: 20px;
					height: 20px;
				}
			}
			.setupicon {
				position: relative;
			}

			.setupicon,
			.multipleSelect {
				width: 30px;
				height: 30px;

				&:hover {
					cursor: pointer;
				}
			}
		}

		.items {
			display: flex;
			flex-direction: column;
			gap: 10px;

			.add-item {
				box-sizing: border-box;
				border: 1px solid #bcbfc3;
				border-radius: 5px;
				width: 100%;
				max-width: 100%;
				height: 83px;
				.inner {
					width: 100%;
					height: 100%;
					display: flex;
					align-items: center;
					justify-content: center;
				}
				.add-sign {
					width: 35px;
					height: 35px;
				}
			}
		}
	}

	@include DesktopSize {
		.container {
			gap: 20px;
			.controler-name {
				.name {
					font-weight: 550;
					font-size: 2.1875rem;
				}

				.icons {
					padding-top: 5px;
					gap: 10px;
					margin-left: 15px;
					.icon {
						width: 33px;
						height: 33px;
					}
				}

				.setupicon,
				.multipleSelect {
					width: 36px;
					height: 36px;
				}
			}

			.items {
				flex-direction: row;
				flex-wrap: wrap;
				gap: 20px;
				max-height: 700px;
				overflow-y: auto;

				.add-item {
					width: 300px;
					height: 130px;
					.add-sign {
						width: 54px;
						height: 54px;
					}
				}

				&::-webkit-scrollbar {
					width: 2px;
				}
				&::-webkit-scrollbar-track {
					background-color: transparent;
				}
				&::-webkit-scrollbar-thumb {
					background-color: #bcbfc3;
				}
			}
		}
	}
</style>
